/*
 * Artwork preview.
 *
 * Nothing here submits, uploads or fetches. The file the customer picks is
 * read locally to draw a crop preview and is then forgotten — the request
 * itself is made through the form further down the page.
 *
 * The canvas is never read back or exported, so it is only ever a preview and
 * never the print file.
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  function fill(template, values) {
    return Object.keys(values).reduce(function (text, key) {
      return text.split('[' + key + ']').join(values[key]);
    }, template || '');
  }

  function create(root) {
    var canvas = root.querySelector('[data-dcs-canvas]');
    var fileInput = root.querySelector('[data-dcs-file]');
    var zoomInput = root.querySelector('[data-dcs-zoom]');
    var warningEl = root.querySelector('[data-dcs-warning]');
    var summaryEl = root.querySelector('[data-dcs-summary]');
    var continueButton = root.querySelector('[data-dcs-continue]');
    var controls = root.querySelector('.dcs-artwork-request__controls');

    if (!canvas || !fileInput) return null;

    var context = canvas.getContext('2d');
    if (!context) return null;

    var maxMb = root.getAttribute('data-max-mb') || '20';
    var maxBytes = parseFloat(maxMb) * 1024 * 1024;
    var minWidth = parseInt(root.getAttribute('data-min-width') || '0', 10);
    var minHeight = parseInt(root.getAttribute('data-min-height') || '0', 10);
    var summaryPrefix = root.getAttribute('data-summary-prefix') || '';
    var formAnchor = root.getAttribute('data-form-anchor') || '';
    var acceptedTypes = (root.getAttribute('data-accepted-types') || '')
      .split(',')
      .map(function (value) {
        return value.trim().toLowerCase();
      })
      .filter(Boolean);

    var text = {
      lowRes: root.getAttribute('data-low-res-text') || '',
      type: root.getAttribute('data-type-error-text') || '',
      size: root.getAttribute('data-size-error-text') || '',
    };

    var listeners = [];
    function register(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push([target, type, handler, options]);
    }

    var artwork = null;
    var artworkUrl = '';
    var template = null;
    var safe = { x: 0, y: 0, width: 100, height: 100 };
    var pan = { x: 0, y: 0 };
    var zoom = 1;

    /* Drawing -------------------------------------------------------------- */

    function safeRect() {
      return {
        x: (safe.x / 100) * canvas.width,
        y: (safe.y / 100) * canvas.height,
        width: (safe.width / 100) * canvas.width,
        height: (safe.height / 100) * canvas.height,
      };
    }

    function artworkBox() {
      var rect = safeRect();
      // Cover the safe area, then apply the customer's zoom on top.
      var scale = Math.max(rect.width / artwork.naturalWidth, rect.height / artwork.naturalHeight) * zoom;
      var width = artwork.naturalWidth * scale;
      var height = artwork.naturalHeight * scale;
      var slackX = Math.max(0, (width - rect.width) / 2);
      var slackY = Math.max(0, (height - rect.height) / 2);

      pan.x = Math.min(slackX, Math.max(-slackX, pan.x));
      pan.y = Math.min(slackY, Math.max(-slackY, pan.y));

      return {
        rect: rect,
        x: rect.x + (rect.width - width) / 2 + pan.x,
        y: rect.y + (rect.height - height) / 2 + pan.y,
        width: width,
        height: height,
      };
    }

    function draw() {
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (artwork) {
        var box = artworkBox();
        context.save();
        context.beginPath();
        context.rect(box.rect.x, box.rect.y, box.rect.width, box.rect.height);
        context.clip();
        context.drawImage(artwork, box.x, box.y, box.width, box.height);
        context.restore();
      }

      if (template) {
        context.drawImage(template, 0, 0, canvas.width, canvas.height);
      } else {
        // No case template set yet: outline the safe area so the crop still reads.
        var outline = safeRect();
        context.save();
        context.strokeStyle = 'rgba(128, 128, 128, 0.8)';
        context.setLineDash([12, 10]);
        context.lineWidth = 4;
        context.strokeRect(outline.x, outline.y, outline.width, outline.height);
        context.restore();
      }
    }

    /* Device, tier and the restated selection ------------------------------ */

    function checkedDevice() {
      return root.querySelector('[data-dcs-device]:checked');
    }

    function checkedTier() {
      return root.querySelector('[data-dcs-tier]:checked');
    }

    function currentModel() {
      var device = checkedDevice();
      if (!device) return null;
      var key = device.getAttribute('data-dcs-device');
      var select = root.querySelector('[data-dcs-model="' + key + '"]');
      return select && select.value ? select.value : null;
    }

    function showModelFor(key) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-dcs-model-wrap]'), function (wrap) {
        wrap.toggleAttribute('hidden', wrap.getAttribute('data-dcs-model-wrap') !== key);
      });
    }

    function refreshSummary() {
      if (!summaryEl) return;

      var device = checkedDevice();
      var tier = checkedTier();
      var parts = [];

      // The model is more specific than the brand, so it replaces it.
      var model = currentModel();
      if (model) {
        parts.push(model);
      } else if (device) {
        parts.push(device.value);
      }
      if (tier) {
        var price = tier.getAttribute('data-price');
        parts.push(price ? tier.value + ' — ' + price : tier.value);
      }

      summaryEl.textContent = parts.join(' · ');
      if (summaryPrefix && !parts.length) summaryEl.textContent = '';
    }

    function applyDevice(radio) {
      if (!radio) return;

      safe = {
        x: parseFloat(radio.getAttribute('data-safe-x') || '0'),
        y: parseFloat(radio.getAttribute('data-safe-y') || '0'),
        width: parseFloat(radio.getAttribute('data-safe-width') || '100'),
        height: parseFloat(radio.getAttribute('data-safe-height') || '100'),
      };

      var width = parseInt(radio.getAttribute('data-template-width') || '0', 10);
      var height = parseInt(radio.getAttribute('data-template-height') || '0', 10);
      if (width > 0 && height > 0) {
        canvas.width = width;
        canvas.height = height;
      }

      var source = radio.getAttribute('data-template');
      template = null;

      if (source) {
        var image = new Image();
        image.onload = function () {
          template = image;
          draw();
        };
        image.src = source;
      }

      draw();
    }

    if (controls) {
      register(controls, 'change', function (event) {
        if (event.target.hasAttribute('data-dcs-device')) {
          applyDevice(event.target);
          showModelFor(event.target.getAttribute('data-dcs-device'));
        }
        if (
          event.target.hasAttribute('data-dcs-device') ||
          event.target.hasAttribute('data-dcs-tier') ||
          event.target.hasAttribute('data-dcs-model')
        ) {
          refreshSummary();
        }
      });
    }

    /* File ----------------------------------------------------------------- */

    function releaseArtwork() {
      if (artworkUrl) {
        URL.revokeObjectURL(artworkUrl);
        artworkUrl = '';
      }
      artwork = null;
    }

    function typeAllowed(file) {
      if (!acceptedTypes.length) return true;
      var name = (file.name || '').toLowerCase();
      var type = (file.type || '').toLowerCase();

      return acceptedTypes.some(function (accepted) {
        if (accepted.charAt(0) === '.') return name.slice(-accepted.length) === accepted;
        if (accepted.slice(-2) === '/*') return type.indexOf(accepted.slice(0, -1)) === 0;
        return type === accepted;
      });
    }

    function setWarning(message) {
      if (warningEl) warningEl.textContent = message || '';
    }

    function rejectFile(message) {
      releaseArtwork();
      fileInput.value = '';
      setWarning(message);
      draw();
    }

    function acceptFile(file) {
      if (!typeAllowed(file)) {
        rejectFile(text.type);
        return;
      }

      if (maxBytes > 0 && file.size > maxBytes) {
        rejectFile(fill(text.size, { max: maxMb }));
        return;
      }

      releaseArtwork();
      artworkUrl = URL.createObjectURL(file);

      var image = new Image();
      image.onload = function () {
        artwork = image;
        pan.x = 0;
        pan.y = 0;
        zoom = 1;
        if (zoomInput) zoomInput.value = '100';

        // Too small to print well is a warning, never a block: we mockup anyway.
        if ((minWidth && image.naturalWidth < minWidth) || (minHeight && image.naturalHeight < minHeight)) {
          setWarning(
            fill(text.lowRes, {
              w: image.naturalWidth,
              h: image.naturalHeight,
              min_w: minWidth,
              min_h: minHeight,
            })
          );
        } else {
          setWarning('');
        }

        draw();
      };
      image.onerror = function () {
        rejectFile(text.type);
      };
      image.src = artworkUrl;
    }

    register(fileInput, 'change', function () {
      if (fileInput.files && fileInput.files.length) acceptFile(fileInput.files[0]);
    });

    /* Drag and drop -------------------------------------------------------- */

    ['dragenter', 'dragover'].forEach(function (type) {
      register(canvas, type, function (event) {
        event.preventDefault();
        canvas.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (type) {
      register(canvas, type, function () {
        canvas.classList.remove('is-dragover');
      });
    });

    register(canvas, 'drop', function (event) {
      event.preventDefault();
      var files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;

      /*
       * Writing the drop back into the real input keeps one source of truth,
       * and keeps the visible file name in step with the preview.
       */
      if (typeof DataTransfer === 'function') {
        var transfer = new DataTransfer();
        transfer.items.add(files[0]);
        fileInput.files = transfer.files;
      }
      acceptFile(files[0]);
    });

    /* Reposition and pinch ------------------------------------------------- */

    var pointers = {};
    var pointerCount = 0;
    var last = { x: 0, y: 0 };
    var pinchStart = 0;
    var pinchZoom = 1;

    function canvasScale() {
      var rect = canvas.getBoundingClientRect();
      return rect.width ? canvas.width / rect.width : 1;
    }

    function pinchDistance() {
      var ids = Object.keys(pointers);
      if (ids.length < 2) return 0;
      var a = pointers[ids[0]];
      var b = pointers[ids[1]];
      return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
    }

    register(canvas, 'pointerdown', function (event) {
      if (!artwork) return;
      if (!pointers[event.pointerId]) pointerCount += 1;
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY };

      if (pointerCount === 2) {
        pinchStart = pinchDistance();
        pinchZoom = zoom;
      } else {
        last.x = event.clientX;
        last.y = event.clientY;
      }

      canvas.setPointerCapture(event.pointerId);
    });

    register(canvas, 'pointermove', function (event) {
      if (!artwork || !pointers[event.pointerId]) return;
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY };

      if (pointerCount >= 2) {
        var distance = pinchDistance();
        if (pinchStart > 0 && distance > 0) {
          zoom = Math.min(3, Math.max(1, (pinchZoom * distance) / pinchStart));
          if (zoomInput) zoomInput.value = String(Math.round(zoom * 100));
          draw();
        }
        return;
      }

      var scale = canvasScale();
      pan.x += (event.clientX - last.x) * scale;
      pan.y += (event.clientY - last.y) * scale;
      last.x = event.clientX;
      last.y = event.clientY;
      draw();
    });

    ['pointerup', 'pointercancel'].forEach(function (type) {
      register(canvas, type, function (event) {
        if (pointers[event.pointerId]) {
          delete pointers[event.pointerId];
          pointerCount = Math.max(0, pointerCount - 1);
        }
        // The finger left behind becomes the new pan anchor.
        var remaining = Object.keys(pointers)[0];
        if (remaining) {
          last.x = pointers[remaining].x;
          last.y = pointers[remaining].y;
        }
        if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      });
    });

    register(canvas, 'keydown', function (event) {
      if (!artwork) return;
      var step = safeRect().width * 0.02;
      var moved = true;

      switch (event.key) {
        case 'ArrowLeft':
          pan.x -= step;
          break;
        case 'ArrowRight':
          pan.x += step;
          break;
        case 'ArrowUp':
          pan.y -= step;
          break;
        case 'ArrowDown':
          pan.y += step;
          break;
        default:
          moved = false;
          break;
      }

      if (!moved) return;
      event.preventDefault();
      draw();
    });

    if (zoomInput) {
      register(zoomInput, 'input', function () {
        zoom = Math.max(1, parseFloat(zoomInput.value || '100') / 100);
        draw();
      });
    }

    /* Continue to the form -------------------------------------------------- */

    function formTarget() {
      if (formAnchor) {
        var named = document.querySelector(formAnchor);
        if (named) return named;
      }
      // The form is added downstream in the theme editor, so fall back to
      // whatever section was placed after this one.
      var section = root.closest('.shopify-section');
      return section ? section.nextElementSibling : null;
    }

    if (continueButton) {
      var target = formTarget();
      if (target) {
        continueButton.removeAttribute('hidden');
        register(continueButton, 'click', function () {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });

          var focusable = target.querySelector(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable) {
            focusable.focus({ preventScroll: true });
            return;
          }
          // Nothing focusable inside: move focus to the region itself.
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      }
    }

    register(window, 'pagehide', releaseArtwork);

    /* Opening state -------------------------------------------------------- */

    function preselect(attribute, value) {
      if (!value) return;
      var match = root.querySelector('[' + attribute + '="' + value.replace(/"/g, '') + '"]');
      if (match) match.checked = true;
    }

    try {
      var params = new URL(window.location.href).searchParams;
      preselect('data-dcs-device', params.get('device'));
      preselect('data-dcs-tier', params.get('tier'));
    } catch (error) {
      // A URL we cannot parse just means no pre-selection.
    }

    applyDevice(checkedDevice());
    if (checkedDevice()) showModelFor(checkedDevice().getAttribute('data-dcs-device'));
    refreshSummary();

    return {
      selectBlock: function (element) {
        var radio = element.querySelector('input[type="radio"]');
        if (!radio) return;
        radio.checked = true;
        if (radio.hasAttribute('data-dcs-device')) applyDevice(radio);
        refreshSummary();
      },
      destroy: function () {
        releaseArtwork();
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2], entry[3]);
        });
        listeners.length = 0;
      },
    };
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-artwork]');

    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      var instance = create(root);
      if (instance) instances.set(root, instance);
    });
  }

  function destroy(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-artwork]');

    Array.prototype.forEach.call(roots, function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var root = target.closest('[data-dcs-artwork]');
    var instance = root && instances.get(root);
    if (!instance) return;

    instance.selectBlock(target);
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
})();
